import { Box } from "@mui/material";

import { useIsMobile } from "../AppLayout/useResponsive";
import { BottomSheet } from "./BottomSheet";
import { Dialog, type DialogProps } from "./Dialog";
import { DialogHeader } from "./DialogHeader";

export interface ResponsiveDialogProps extends DialogProps {
  /**
   * Width of the dialog in pixels (desktop only, ignored on mobile)
   * @default 400
   */
  width?: number;
  /** When false, prevents closing via swipe and backdrop click on mobile. Defaults to true. */
  dismissible?: boolean;
  /** Custom close handler. Called when X button is clicked. Defaults to modalState.closeModal. */
  onClose?: () => void;
}

/**
 * Responsive dialog component that adapts to screen size.
 *
 * - Desktop (>= md): Renders as centered Dialog
 * - Mobile (< md): Renders as bottom sheet
 *
 * Features:
 * - Automatic responsive behavior
 * - Unified API for both variants
 * - Consistent header, content, and footer structure
 *
 * @example
 * ```tsx
 * <ResponsiveDialog
 *   modalState={modalState}
 *   title="Confirm action"
 *   footerContent={<DialogFooter ... />}
 * >
 *   <Text>Are you sure?</Text>
 * </ResponsiveDialog>
 * ```
 */
export function ResponsiveDialog(props: ResponsiveDialogProps) {
  const {
    children,
    modalState,
    title,
    showCloseButton = true,
    footerContent,
    sx,
    dismissible,
    onClose,
  } = props;

  const isMobile = useIsMobile();
  const handleClose = onClose ?? modalState.closeModal;

  if (isMobile) {
    return (
      <BottomSheet modalState={modalState} sx={sx} dismissible={dismissible}>
        {title && (
          <DialogHeader showCloseButton={showCloseButton} title={title} onClose={handleClose} />
        )}
        <Box
          sx={(theme) => ({
            padding: theme.spacing(5),
            overflowY: "auto",
            flex: 1,
          })}
        >
          {children}
        </Box>
        {footerContent && (
          <Box
            sx={(theme) => ({
              borderTop: `1px solid ${theme.palette.border?.base}`,
            })}
          >
            {footerContent}
          </Box>
        )}
      </BottomSheet>
    );
  }

  return <Dialog {...props} />;
}
